/**
 * Simple markdown to HTML converter for streaming AI content.
 * Handles common markdown patterns used in AI-generated articles.
 */

// Video embed marker format: {{VIDEO_EMBED:platform:videoId}}
const VIDEO_EMBED_MARKER_REGEX = /\{\{VIDEO_EMBED:(\w+):([^}]+)\}\}/g

// Video platform configurations for generating embed URLs
const VIDEO_PLATFORM_EMBED_URLS: Record<string, (videoId: string) => string> = {
  youtube: (videoId) => `https://www.youtube.com/embed/${videoId}`,
  loom: (videoId) => `https://www.loom.com/embed/${videoId}`,
  vimeo: (videoId) => `https://player.vimeo.com/video/${videoId}`,
  wistia: (videoId) => `https://fast.wistia.net/embed/iframe/${videoId}`,
}

/**
 * Converts markdown text to HTML.
 * Supports: headings, bold, italic, links, lists, code blocks, inline code, blockquotes, horizontal rules, video embeds
 */
export function markdownToHtml(markdown: string): string {
  if (!markdown) return ''

  let html = markdown

  // Convert video embed markers to HTML
  // {{VIDEO_EMBED:youtube:dQw4w9WgXcQ}} -> video embed div
  html = html.replace(VIDEO_EMBED_MARKER_REGEX, (_, platform, videoId) => {
    // Validate videoId is safe (alphanumeric, hyphens, underscores)
    if (!/^[\w-]+$/.test(videoId)) return ''
    const getEmbedUrl = VIDEO_PLATFORM_EMBED_URLS[platform]
    if (!getEmbedUrl) return ''
    const embedUrl = getEmbedUrl(videoId)
    return `<div class="video-embed" data-video-src="${embedUrl}" data-video-platform="${platform}" contenteditable="false" style="position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden; max-width: 100%; margin: 1em 0; border-radius: 8px; background-color: #1a1a2e;"><iframe src="${embedUrl}" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: 0; border-radius: 8px;" frameborder="0" allowfullscreen="true" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin"></iframe></div>`
  })

  // Escape stray `&` characters, but leave any already-valid HTML entity
  // untouched (named like &nbsp; / &mdash;, decimal like &#39;, or hex like &#x27;).
  // The old narrow exclusion list (amp/lt/gt/quot/#) double-escaped common
  // entities such as &nbsp;, which then rendered as literal text in preview.
  html = html.replace(/&(?![a-zA-Z][a-zA-Z0-9]*;|#\d+;|#x[\dA-Fa-f]+;)/g, '&amp;')

  // Code blocks (must be processed before other patterns)
  // ```language\ncode\n```
  html = html.replace(/```(\w*)\n([\s\S]*?)```/g, (_, lang, code) => {
    const escapedCode = code.replace(/</g, '&lt;').replace(/>/g, '&gt;').trim()
    return `<pre><code class="language-${lang || 'text'}">${escapedCode}</code></pre>`
  })

  // Inline code (must be before other inline patterns)
  // `code` — restricted to a single line so an unclosed backtick doesn't
  // swallow the rest of the document.
  html = html.replace(/`([^`\n]+)`/g, '<code>$1</code>')

  // Headings
  // ## Heading
  html = html.replace(/^######\s+(.*)$/gm, '<h6>$1</h6>')
  html = html.replace(/^#####\s+(.*)$/gm, '<h5>$1</h5>')
  html = html.replace(/^####\s+(.*)$/gm, '<h4>$1</h4>')
  html = html.replace(/^###\s+(.*)$/gm, '<h3>$1</h3>')
  html = html.replace(/^##\s+(.*)$/gm, '<h2>$1</h2>')
  html = html.replace(/^#\s+(.*)$/gm, '<h1>$1</h1>')

  // Bold and italic combinations
  // ***text*** or ___text___
  html = html.replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
  html = html.replace(/___(.+?)___/g, '<strong><em>$1</em></strong>')

  // Bold
  // **text** or __text__
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
  html = html.replace(/__(.+?)__/g, '<strong>$1</strong>')

  // Italic — restricted to a single line to avoid swallowing line-anchored
  // markdown like `* item\n* item` or `_ item_` into an italic span.
  // *text* or _text_
  html = html.replace(/\*([^*\n]+)\*/g, '<em>$1</em>')
  html = html.replace(/_([^_\n]+)_/g, '<em>$1</em>')

  // Strikethrough
  // ~~text~~
  html = html.replace(/~~(.+?)~~/g, '<del>$1</del>')

  // Links — single-line only so stray brackets in adjacent paragraphs don't
  // produce a runaway match.
  // [text](url)
  html = html.replace(
    /\[([^\]\n]+)\]\(([^)\n]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
  )

  // Images — same single-line constraint.
  // ![alt](url)
  html = html.replace(/!\[([^\]\n]*)\]\(([^)\n]+)\)/g, '<img src="$2" alt="$1" />')

  // Blockquotes
  // > quote
  html = html.replace(/^>\s+(.*)$/gm, '<blockquote>$1</blockquote>')
  // Merge consecutive blockquotes
  html = html.replace(/<\/blockquote>\n<blockquote>/g, '\n')

  // Horizontal rules
  // --- or *** or ___
  html = html.replace(/^([-*_]){3,}$/gm, '<hr />')

  html = html.replace(/^[\s]*[-*]\s+(.*)$/gm, '<li data-l="ul">$1</li>')
  html = html.replace(/^[\s]*\d+\.\s+(.*)$/gm, '<li data-l="ol">$1</li>')

  html = html.replace(/(<li data-l="ul">[\s\S]*?<\/li>\n?)+/g, (m) => `<ul>${m}</ul>`)
  html = html.replace(/(<li data-l="ol">[\s\S]*?<\/li>\n?)+/g, (m) => `<ol>${m}</ol>`)
  html = html.replace(/ data-l="(?:ul|ol)"/g, '')
  const lines = html.split('\n')
  const processedLines = lines.map((line) => {
    const trimmed = line.trim()
    if (!trimmed) return ''
    // Skip if already a block-ish element (block or self-rendering media).
    if (
      trimmed.startsWith('<h') ||
      trimmed.startsWith('<p') ||
      trimmed.startsWith('<ul') ||
      trimmed.startsWith('<ol') ||
      trimmed.startsWith('<li') ||
      trimmed.startsWith('<blockquote') ||
      trimmed.startsWith('<pre') ||
      trimmed.startsWith('<hr') ||
      trimmed.startsWith('</') ||
      trimmed.startsWith('<div') ||
      trimmed.startsWith('<table') ||
      trimmed.startsWith('<img') ||
      trimmed.startsWith('<video') ||
      trimmed.startsWith('<audio') ||
      trimmed.startsWith('<iframe') ||
      trimmed.startsWith('<figure')
    ) {
      return line
    }
    return `<p>${trimmed}</p>`
  })

  html = processedLines.filter((l) => l !== '').join('\n')

  // Clean up multiple newlines
  html = html.replace(/\n{3,}/g, '\n\n')

  return html.trim()
}

// Extract video ID from embed URL
function extractVideoIdFromEmbedUrl(embedUrl: string, platform: string): string | null {
  try {
    const url = new URL(embedUrl)
    switch (platform) {
      case 'youtube':
        // https://www.youtube.com/embed/VIDEO_ID
        return url.pathname.split('/').pop() || null
      case 'loom':
        // https://www.loom.com/embed/VIDEO_ID
        return url.pathname.split('/').pop() || null
      case 'vimeo':
        // https://player.vimeo.com/video/VIDEO_ID
        return url.pathname.split('/').pop() || null
      case 'wistia':
        // https://fast.wistia.net/embed/iframe/VIDEO_ID
        return url.pathname.split('/').pop() || null
      default:
        return null
    }
  } catch {
    return null
  }
}

// Extract data-video-src and data-video-platform from a div tag string
function extractVideoEmbedData(divTag: string): { embedUrl: string; platform: string } | null {
  const srcMatch = divTag.match(/data-video-src="([^"]*)"/)
  const platformMatch = divTag.match(/data-video-platform="([^"]*)"/)

  if (srcMatch?.[1] && platformMatch?.[1]) {
    return { embedUrl: srcMatch[1], platform: platformMatch[1] }
  }
  return null
}

/**
 * Converts HTML back to markdown (for storage/API).
 * This is a basic converter for common patterns.
 */
export function htmlToMarkdown(html: string): string {
  if (!html) return ''

  let markdown = html

  // Convert video embed divs to markers BEFORE stripping other divs
  // This preserves video embeds during the conversion
  // Use a flexible regex that matches any div with class="video-embed" and extracts attributes
  markdown = markdown.replace(
    /<div[^>]*class="[^"]*video-embed[^"]*"[^>]*>[\s\S]*?<\/div>/gi,
    (match) => {
      const data = extractVideoEmbedData(match)
      if (data) {
        const videoId = extractVideoIdFromEmbedUrl(data.embedUrl, data.platform)
        if (videoId) {
          return `\n{{VIDEO_EMBED:${data.platform}:${videoId}}}\n`
        }
      }
      return ''
    }
  )

  // Also handle TinyMCE's mce-preview-object wrapper around video embeds
  // TinyMCE may wrap our video-embed div in a span with class mce-preview-object or mce-object-video
  markdown = markdown.replace(
    /<span[^>]*class="[^"]*mce-(?:preview-object|object-video)[^"]*"[^>]*>[\s\S]*?<\/span>/gi,
    (match) => {
      // Check if this contains a video-embed
      const data = extractVideoEmbedData(match)
      if (data) {
        const videoId = extractVideoIdFromEmbedUrl(data.embedUrl, data.platform)
        if (videoId) {
          return `\n{{VIDEO_EMBED:${data.platform}:${videoId}}}\n`
        }
      }
      // Not a video embed wrapper, return empty (will be handled by span stripping)
      return ''
    }
  )

  // Remove any wrapping divs or spans that TinyMCE might add
  markdown = markdown.replace(/<div[^>]*>/gi, '\n')
  markdown = markdown.replace(/<\/div>/gi, '\n')
  markdown = markdown.replace(/<span[^>]*>/gi, '')
  markdown = markdown.replace(/<\/span>/gi, '')

  // Headings
  markdown = markdown.replace(/<h1[^>]*>(.*?)<\/h1>/gi, '# $1\n')
  markdown = markdown.replace(/<h2[^>]*>(.*?)<\/h2>/gi, '## $1\n')
  markdown = markdown.replace(/<h3[^>]*>(.*?)<\/h3>/gi, '### $1\n')
  markdown = markdown.replace(/<h4[^>]*>(.*?)<\/h4>/gi, '#### $1\n')
  markdown = markdown.replace(/<h5[^>]*>(.*?)<\/h5>/gi, '##### $1\n')
  markdown = markdown.replace(/<h6[^>]*>(.*?)<\/h6>/gi, '###### $1\n')

  // Bold and italic
  markdown = markdown.replace(/<strong><em>(.*?)<\/em><\/strong>/gi, '***$1***')
  markdown = markdown.replace(/<em><strong>(.*?)<\/strong><\/em>/gi, '***$1***')
  markdown = markdown.replace(/<strong[^>]*>(.*?)<\/strong>/gi, '**$1**')
  markdown = markdown.replace(/<b[^>]*>(.*?)<\/b>/gi, '**$1**')
  markdown = markdown.replace(/<em[^>]*>(.*?)<\/em>/gi, '*$1*')
  markdown = markdown.replace(/<i[^>]*>(.*?)<\/i>/gi, '*$1*')

  // Strikethrough
  markdown = markdown.replace(/<del[^>]*>(.*?)<\/del>/gi, '~~$1~~')
  markdown = markdown.replace(/<s[^>]*>(.*?)<\/s>/gi, '~~$1~~')

  // Links
  markdown = markdown.replace(/<a[^>]*href="([^"]*)"[^>]*>(.*?)<\/a>/gi, '[$2]($1)')

  // Images
  markdown = markdown.replace(/<img[^>]*src="([^"]*)"[^>]*alt="([^"]*)"[^>]*\/?>/gi, '![$2]($1)')
  markdown = markdown.replace(/<img[^>]*alt="([^"]*)"[^>]*src="([^"]*)"[^>]*\/?>/gi, '![$1]($2)')

  // Code blocks
  markdown = markdown.replace(
    /<pre[^>]*><code[^>]*class="language-([^"]*)"[^>]*>([\s\S]*?)<\/code><\/pre>/gi,
    '```$1\n$2\n```'
  )
  markdown = markdown.replace(/<pre[^>]*><code[^>]*>([\s\S]*?)<\/code><\/pre>/gi, '```\n$1\n```')
  markdown = markdown.replace(/<pre[^>]*>([\s\S]*?)<\/pre>/gi, '```\n$1\n```')

  // Inline code
  markdown = markdown.replace(/<code[^>]*>(.*?)<\/code>/gi, '`$1`')

  // Blockquotes
  markdown = markdown.replace(/<blockquote[^>]*>([\s\S]*?)<\/blockquote>/gi, (_, content) => {
    return content
      .split('\n')
      .map((line: string) => `> ${line.trim()}`)
      .join('\n')
  })

  // Lists — preserve ordered vs unordered semantics. Convert each <li>
  // inside an <ol> to a numbered item (`N. item`); each <li> inside a <ul>
  // becomes a bulleted item (`- item`). Any stray <li> left over (no
  // recognised parent) defaults to a bullet.
  markdown = markdown.replace(/<ol[^>]*>([\s\S]*?)<\/ol>/gi, (_, inner: string) => {
    let n = 0
    return inner.replace(/<li[^>]*>([\s\S]*?)<\/li>/gi, (__, item: string) => {
      n += 1
      return `${n}. ${item.trim()}\n`
    })
  })
  markdown = markdown.replace(/<ul[^>]*>([\s\S]*?)<\/ul>/gi, (_, inner: string) =>
    inner.replace(/<li[^>]*>([\s\S]*?)<\/li>/gi, (__, item: string) => `- ${item.trim()}\n`)
  )
  // Fallback for any orphan <li> outside <ul>/<ol>.
  markdown = markdown.replace(/<li[^>]*>(.*?)<\/li>/gi, '- $1\n')

  // Horizontal rules
  markdown = markdown.replace(/<hr[^>]*\/?>/gi, '---\n')

  // Paragraphs and line breaks
  // Handle empty paragraphs first (TinyMCE uses <p><br></p> or <p>&nbsp;</p> for empty lines)
  markdown = markdown.replace(/<p[^>]*>\s*<br[^>]*\/?>\s*<\/p>/gi, '\n')
  markdown = markdown.replace(/<p[^>]*>&nbsp;<\/p>/gi, '\n')
  markdown = markdown.replace(/<p[^>]*>\s*<\/p>/gi, '\n')
  // Regular paragraphs with content
  markdown = markdown.replace(/<p[^>]*>(.*?)<\/p>/gi, '$1\n\n')
  markdown = markdown.replace(/<br[^>]*\/?>/gi, '\n')

  // Decode HTML entities
  markdown = markdown.replace(/&amp;/g, '&')
  markdown = markdown.replace(/&lt;/g, '<')
  markdown = markdown.replace(/&gt;/g, '>')
  markdown = markdown.replace(/&quot;/g, '"')
  markdown = markdown.replace(/&#39;/g, "'")
  markdown = markdown.replace(/&nbsp;/g, ' ')

  // Clean up extra whitespace
  markdown = markdown.replace(/\n{3,}/g, '\n\n')
  markdown = markdown.trim()

  return markdown
}

export function resolveArticleContent(stored: string): string {
  return stored.trimStart().startsWith('<') ? stored : markdownToHtml(stored)
}

export default markdownToHtml
